import { useState } from 'react'
import toast from 'react-hot-toast'

/**
 * Inline-editable name: shows the text with a discreet ✏️; clicking switches
 * to an input that saves on Enter / blur and cancels on Escape. The parent
 * supplies `onSave(name)` returning a promise (the rename mutation).
 */
export function EditableName({
  value,
  onSave,
  className = '',
  inputClassName = '',
}: {
  value: string
  onSave: (name: string) => Promise<unknown>
  className?: string
  inputClassName?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  const start = () => {
    setDraft(value)
    setEditing(true)
  }

  const commit = async () => {
    const trimmed = draft.trim()
    if (saving) return
    if (trimmed === value || trimmed.length < 2) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(trimmed)
      setEditing(false)
    } catch (e) {
      toast.error((e as Error).message || 'Renommage impossible')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        disabled={saving}
        maxLength={60}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void commit()
          if (e.key === 'Escape') setEditing(false)
        }}
        className={`bg-ocean-900 border border-sand-300/50 rounded px-2 py-0.5 focus:outline-none ${inputClassName}`}
      />
    )
  }

  return (
    <button
      onClick={start}
      title="Renommer"
      className={`group inline-flex items-center gap-1.5 text-left hover:text-sand-100 transition ${className}`}
    >
      <span>{value}</span>
      <span className="text-xs opacity-0 group-hover:opacity-60 transition">✏️</span>
    </button>
  )
}
